import { InboxOutlined } from "@ant-design/icons";
import { useMutation } from "@apollo/client";
import { Button, message, Modal, notification, Spin, Tooltip } from "antd";
import { RcFile, UploadFile, UploadProps } from "antd/lib/upload";
import Dragger from "antd/lib/upload/Dragger";
import { ErrorMessages, SidepanelStyles } from "constants/index";
import { ProjectContext } from "context/ProjectProvider";
import { useContext, useState } from "react";
import { noAuthClient } from "services/axios";
import { generateS3URL } from "services/file-mgmt";
import { MUTATION_INSERT_SPECIFICATION } from "services/graphQL/mutations";
import { extractUploadDrawingAPI } from "../auth/extraction-auth";

export default function ImportExtractionDrawing({
  openDrawer,
  onCancel,
  onComplete
}: {
  openDrawer: boolean;
  onCancel: () => void;
  onComplete: (taskId: string) => void;
}) {
  const { tokenRetrievalState, gqlClientForProject, projectDetails } =
    useContext(ProjectContext);

  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [unsupportedFileSelected, setUnsupportedFileSelected] = useState(false);

  const [insertSpecification] = useMutation(MUTATION_INSERT_SPECIFICATION, {
    client: gqlClientForProject
  });

  const [uploadingState, setUploadingState] = useState<{
    uploading: boolean;
    processingStatus: string;
  }>({
    uploading: false,
    processingStatus: ""
  });

  const props: UploadProps = {
    accept: "application/pdf",
    style: { borderColor: unsupportedFileSelected === true ? "red" : "" },
    multiple: false,
    fileList,
    onChange: (event: any) => {
      console.log("event ", event);
    },
    onRemove: () => {
      setFileList([]);
    },
    onDrop: (e) => {
      if (e.dataTransfer.files.length > 0) {
        const file = e.dataTransfer.files[0];
        if (file.type !== "application/pdf") {
          setUnsupportedFileSelected(true);
          setFileList([]);
          message.error(ErrorMessages.specUploads.pdfSelectionOnly);
          return false;
        }
        setUnsupportedFileSelected(false);
        return false;
      }
      return false;
    },
    beforeUpload: (file: RcFile) => {
      if (file.type !== "application/pdf") {
        setUnsupportedFileSelected(true);
        setFileList([]);
        message.error(ErrorMessages.specUploads.pdfSelectionOnly);
        return false;
      }
      setUnsupportedFileSelected(false);
      setFileList([file]);
      return false;
    },
    showUploadList: {
      showDownloadIcon: false,
      showPreviewIcon: false
    }
  };

  const handleUpload = async () => {
    setUploadingState({
      uploading: true,
      processingStatus: ""
    });
    const selectedFile: any = fileList[0];

    const specPayload: any = {
      specification: {
        type: "DRAWING",
        title: selectedFile.name
      }
    };

    try {
      const insertSpecResponse = await insertSpecification({
        variables: { ...specPayload }
      });

      if (
        insertSpecResponse.errors ||
        !insertSpecResponse?.data?.insert_specifications_one?.id
      ) {
        message.error(
          insertSpecResponse.errors?.[0]?.message || "Import failed"
        );
        setUploadingState({
          uploading: false,
          processingStatus: ""
        });
        return;
      }
      setUploadingState({
        uploading: false,
        processingStatus: "PROCESSING"
      });

      const specificationId =
        insertSpecResponse.data.insert_specifications_one.id;

      const signedUrlRespose: any = await generateS3URL(
        {
          feature: "DRAWING",
          feature_id: specificationId,
          file_name: selectedFile.name
        },
        tokenRetrievalState.token
      );

      await noAuthClient.put(
        `${signedUrlRespose.data.success.url}`,
        selectedFile
      );
      setUploadingState({
        uploading: false,
        processingStatus: "PROCESSED"
      });

      const res = await extractUploadDrawingAPI({
        specificationId,
        signedUrl: signedUrlRespose.data.success.url,
        projectId: projectDetails?.id!
      });

      console.log("extraction trigger response: ", res);

      if (res.data) {
        onComplete(specificationId);
        notification.open({
          message: "Material extraction started",
          description:
            "You will receive a notification when the process is completed.",
          placement: "topRight",
          duration: 10
        });
      } else {
        message.error("Failed to trigger extraction process");
      }
    } catch (ex: any) {
      console.log("upload error: ", ex);
      const networkErrMsg = ex.message;
      const serverErrMsg = ex.response?.data?.error;
      message.error(`Import failed: ${serverErrMsg || networkErrMsg}`);

      onCancel();
    }
    setUploadingState({
      uploading: false,
      processingStatus: ""
    });
  };

  return (
    <Modal
      className="custom-drawer"
      title="Extract from drawings"
      width={420}
      style={SidepanelStyles.style}
      bodyStyle={{ height: "calc(100vh - 58px)" }}
      footer={null}
      open={openDrawer}
      onCancel={() => {
        onCancel();
      }}
      destroyOnClose
      data-testid="index-modal-extract-material-log"
    >
      <div className="space-y-4 w-full px-3">
        <div className="w-full">
          <Dragger
            {...props}
            disabled={
              uploadingState.uploading || uploadingState.processingStatus !== ""
            }
            data-testid="index-dragger-upload"
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            {fileList.length > 0 ? (
              <div className="flex px-3 justify-center items-center">
                <Tooltip title={fileList[0].name}>
                  <p
                    className="ant-upload-text truncate max-w-[330px]"
                    data-testid="index-uploaded-filename"
                  >
                    {fileList[0].name}
                  </p>
                </Tooltip>
              </div>
            ) : (
              <div>
                <p
                  className="ant-upload-text"
                  data-testid="index-upload-instruction"
                >
                  Click or drag file to upload.
                </p>
              </div>
            )}
          </Dragger>
        </div>
        <div className="flex justify-center">
          {uploadingState.processingStatus === "" && (
            <Button
              type="primary"
              disabled={!fileList.length || uploadingState.uploading}
              onClick={handleUpload}
              loading={uploadingState.uploading}
              data-testid="index-button-extract"
            >
              Extract materials
            </Button>
          )}

          {uploadingState.processingStatus === "PROCESSING" && (
            <Spin
              className="mt-4 text-red-500"
              tip="File is being uploaded. Please do not close page while processing"
              size="default"
              data-testid="index-spin-processing"
            />
          )}
        </div>
      </div>
    </Modal>
  );
}
